import { useMemo, useState } from "react";
import "./GatewayPage.css";
import StackPickerPopup from "./StackPickerPopup";

/**
 * GatewayPage - the entry launcher (her plan, agreed on the 18-Sep call).
 *
 * The CEO-approved prototype home page is PROMOTED to the front door rather
 * than replaced: full-screen on login, NO left rail / navigation trail
 * visible. The "one workspace" navy hero is the backdrop; the intelligence-
 * module cards act as launcher tiles (Vera's "Where would you like to
 * work?" pattern). Clicking a tile lands inside the app shell, where the
 * rail appears.
 *
 * Compensation is the one two-step tile: it first opens the "browse the
 * roles" rectangles popup (Finance · N roles · M territories ...); picking a
 * vertical routes to /comp?stack=<id> so the page
 * lands already filtered - never on the raw all-roles table.
 *
 * Deliberate choices from the call:
 *  - tile says "Compensation", not "Market range & distribution";
 *  - the Agent is a first-class tile (her point: it must be visible without
 *    digging through navigation);
 *  - always shown on load, two clicks for returning users too - shipped
 *    this way on purpose to collect stakeholder feedback before adding any
 *    skip/remember behavior.
 */
export default function GatewayPage({ onEnter }) {
  // The rectangles popup now owns its own reference data (StackPickerPopup),
  // so the gateway itself only tracks whether it is open.
  const [showStacks, setShowStacks] = useState(false);

  function pickStack(id) {
    // Shell turns this into /comp?stack=<id> - the URL is the only place
    // the vertical lives, so it survives a refresh and travels with the
    // rail to Blended Roles.
    setShowStacks(false);
    onEnter("comp", id);
  }

  const tiles = useMemo(
    () => [
      {
        key: "agent",
        title: "Job Matching Agent",
        badge: "BETA",
        desc: "Describe a role in plain language; get ranked job-code matches with rationale and fit scores.",
        icon: <path d="M21 12a8 8 0 1 1-3.1-6.3L21 4l-1 3.4A8 8 0 0 1 21 12z" />,
        onClick: () => onEnter("agent"),
      },
      {
        key: "comp",
        title: "Compensation",
        badge: "LIVE",
        desc: "Full pay range and core hire band per territory - browse the roles by vertical.",
        icon: <path d="M4 20V9M10 20V4M16 20v-9M21 20H3" />,
        onClick: () => setShowStacks(true),
      },
      {
        key: "trends",
        title: "Trends",
        badge: "LIVE",
        desc: "Job wages vs inflation from 2021 with forecast paths, per territory.",
        icon: <><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></>,
        onClick: () => onEnter("trends"),
      },
      {
        key: "compare",
        title: "Compare",
        badge: "LIVE",
        desc: "Blended roles - match a JD to up to three roles and blend their cost.",
        icon: <><path d="M12 3v18" /><path d="M5 7h5M5 12h5M5 17h5M14 7h5M14 12h5M14 17h5" /></>,
        onClick: () => onEnter("compare"),
      },
      {
        key: "skills",
        title: "JD & Skills",
        badge: "LIVE",
        desc: "Job descriptions side by side; skills matrices aligned across roles.",
        icon: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></>,
        onClick: () => onEnter("skills"),
      },
    ],
    [onEnter]
  );

  return (
    <div className="gw-page">
      <div className="gw-inner">
        <div className="gw-brand">
          <img src="/potentiam_logo.png" alt="Potentiam" />
          <span className="gw-mark">Minerva<em>Intelligence</em></span>
        </div>

        <h1 className="gw-headline">One workspace for every compensation decision.</h1>
        <p className="gw-sub">Where would you like to start?</p>

        <div className="gw-tiles">
          {tiles.map((t) => (
            <button key={t.key} type="button" className="gw-tile" onClick={t.onClick}>
              <div className="gw-tile-top">
                <span className="gw-tile-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{t.icon}</svg>
                </span>
                <span className={`gw-badge${t.badge === "BETA" ? " gw-badge-beta" : ""}`}>{t.badge}</span>
              </div>
              <div className="gw-tile-title">{t.title}</div>
              <div className="gw-tile-desc">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Browse-the-roles rectangles popup (the Comp two-step). Shared with
          the Comp page's own first-arrival popup - StackPickerPopup.jsx. */}
      {showStacks && (
        <StackPickerPopup
          onPick={pickStack}
          onClose={() => setShowStacks(false)}
          onSkip={() => { setShowStacks(false); onEnter("comp"); }}
        />
      )}
    </div>
  );
}
